import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Pause,
  Play,
  Calendar,
  LogOut,
  MapPin,
  Search,
  Filter,
  Menu,
  X,
} from "lucide-react";
import Sidebar from "./BusinessSidebar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
//import ".../styles/Login.css";
import Modal from "./Modal";
import { API_ENDPOINTS } from "../../lib/config";
import { fetchWithAuth } from "../../lib/authService";
import AddEventFormFields from "./AddEventFormFields";
import MobileNBottomNav from "./MobileBottomNav";

// Add CSS for shaking animation
const shakeKeyframes = `
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
  20%, 40%, 60%, 80% { transform: translateX(2px); }
}

.shake-animation {
  animation: shake 0.82s cubic-bezier(.36,.07,.19,.97) both infinite;
}`;

// Add style tag to document head
const styleSheet = document.createElement("style");
styleSheet.innerText = shakeKeyframes;
document.head.appendChild(styleSheet);

export default function BusinessLoggedInArtist() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({});
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sidebarPage, setSidebarPage] = useState("home");
  const [activeFilter, setActiveFilter] = useState("all");
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showPauseConfirm, setShowPauseConfirm] = useState(false);
  const [showResumeConfirm, setShowResumeConfirm] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [showLiveConfirm, setShowLiveConfirm] = useState(false);
  const [showEndLiveConfirm, setShowEndLiveConfirm] = useState(false);
  const [selectedEventForLive, setSelectedEventForLive] = useState(null);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extensionTime, setExtensionTime] = useState(15);
  const [extensionUnit, setExtensionUnit] = useState("minutes");

  const [venues, setVenues] = useState([]); // Make sure this is an array
  const [selectedVenueId, setSelectedVenueId] = useState(""); // venue.id from venues array
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Add timeOptions constant
  const timeOptions = [
    { value: 15, unit: "minutes", label: "15 mins" },
    { value: 30, unit: "minutes", label: "30 mins" },
    { value: 1, unit: "hours", label: "1 hour" },
    { value: 2, unit: "hours", label: "2 hours" },
    { value: 3, unit: "hours", label: "3 hours" },
    { value: 4, unit: "hours", label: "4 hours" },
  ];

  const fetchEvents = async (reset = false) => {
    const token = localStorage.getItem("tringAuthToken");
    if (!token) {
      navigate("/");
      return;
    }

    const currentSkip = reset ? 0 : skip;

    try {
      let url;

      if (!selectedVenueId) {
        url = `${API_ENDPOINTS.EVENT_PERFORMER}?showBusiness=true&skip=${currentSkip}&limit=${limit}`;
      } else {
        url = API_ENDPOINTS.EVENT_VENUE(
          selectedVenueId,
          localStorage.getItem("PerformerId"),
        );
      }

      const evRes = await fetchWithAuth(url);
      if (!evRes.ok) throw new Error(`HTTP error! status: ${evRes.status}`);
      const evData = await evRes.json();

      const eventsArray = Array.isArray(evData) ? evData : [];

      if (reset) {
        setEvents((prev) => {
          const merged = new Map(prev.map((e) => [e.id, e]));
          eventsArray.forEach((e) =>
            merged.set(e.id, { ...merged.get(e.id), ...e }),
          );
          const mergedArr = Array.from(merged.values());
          setSkip(mergedArr.length); // ✅ skip = total events count
          return mergedArr;
        });
      } else {
        setEvents((prev) => {
          const merged = new Map(prev.map((e) => [e.id, e]));
          eventsArray.forEach((e) =>
            merged.set(e.id, { ...merged.get(e.id), ...e }),
          );
          const mergedArr = Array.from(merged.values());
          setSkip(mergedArr.length); // ✅ skip = total events count
          return mergedArr;
        });
      }

      setHasMore(eventsArray.length === limit);
    } catch (err) {
      console.error("Error fetching events:", err);
      setHasMore(false);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  const fetchMoreEvents = () => {
    if (hasMore && !isLoadingMore && !selectedVenueId) {
      setIsLoadingMore(true);
      fetchEvents(false);
    }
  };

  useEffect(() => {
    let intervalId;

    const fetchVenues = async () => {
      try {
        const venuesRes = await fetchWithAuth(API_ENDPOINTS.VENUE_PERFORMERS);
        const venuesData = await venuesRes.json();
        const allVenues = venuesData.venues.map((v) => v.venue);
        setVenues(allVenues);
      } catch (err) {
        setVenues([]);
      }
    };
    fetchVenues();
    intervalId = setInterval(fetchVenues, 4000);
    return () => clearInterval(intervalId);
  }, []);

  // Fetch events when selectedVenueId changes or on initial load
  useEffect(() => {
    let intervalId;

    const fetchEventsInitial = async () => {
      setLoading(true);
      await fetchEvents(true);
    };

    fetchEventsInitial();

    if (!selectedVenueId) {
      intervalId = setInterval(() => {
        fetchEvents(true);
      }, 4000);
    }

    return () => clearInterval(intervalId);
  }, [selectedVenueId]);

  const handleVenueChange = (e) => {
    setSelectedVenueId(e.target.value);
  };

  const formatDate = (iso) => {
    if (!iso) return "Date not available";
    try {
      const date = new Date(iso);
      if (isNaN(date.getTime())) return "Invalid date";

      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (err) {
      console.error("Error formatting date:", err);
      return "Invalid date";
    }
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    try {
      const date = new Date(iso);
      if (isNaN(date.getTime())) return "";

      return date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (err) {
      console.error("Error formatting time:", err);
      return "";
    }
  };

  const displayed = useMemo(() => {
    // Enrich events with venue details
    const enrichEvent = (event) => {
      const venue = venues.find((v) => v.id === event.venueId);
      return {
        ...event,
        venuePhotoUrl: venue?.photoUrl,
        venueLogoUrl: venue?.logoUrl,
        venueAddress: venue?.address,
        venueName: venue?.name,
      };
    };

    let filtered = events.map(enrichEvent);

    if (activeFilter !== "all") {
      filtered = filtered.filter((event) => event.status === activeFilter);
    }

    if (selectedVenueId) {
      filtered = filtered.filter((event) => event.venueId === selectedVenueId);
    }

    if (searchTerm) {
      filtered = filtered.filter((v) => {
        const searchString = (v.name + " " + (v.venueName || "")).toLowerCase();
        return searchString.includes(searchTerm.toLowerCase());
      });
    }

    return filtered.sort((a, b) => {
      const getStatusPriority = (status) => {
        switch (status) {
          case "active":
            return 0;
          case "paused":
            return 1;
          case "approved":
            return 2;
          case "requested":
            return 3;
          case "rejected":
            return 4;
          case "finished":
            return 5;
          default:
            return 6;
        }
      };
      const statusPriorityA = getStatusPriority(a.status);
      const statusPriorityB = getStatusPriority(b.status);
      if (statusPriorityA !== statusPriorityB) {
        return statusPriorityA - statusPriorityB;
      }
      const dateA = new Date(a.startAt);
      const dateB = new Date(b.startAt);
      const currentDate = new Date("2025-06-17T07:46:06Z");
      if (dateA > currentDate && dateB > currentDate) return dateB - dateA;
      if (dateA <= currentDate && dateB <= currentDate) return dateA - dateB;
      return dateA > currentDate ? -1 : 1;
    });
  }, [events, venues, searchTerm, activeFilter, selectedVenueId]);

  // Add this useEffect for search and filter changes
  useEffect(() => {
    setSkip(0);
    setHasMore(true);
    fetchEvents(true);
  }, [searchTerm, activeFilter]); // Add searchTerm and activeFilter as dependencies

  useEffect(() => {
    console.log("Events:", events);
    console.log("Displayed:", displayed);
  }, [events, displayed]);

  const fetchData = async () => {
    const token = localStorage.getItem("tringAuthToken");
    if (!token) {
      navigate("/");
      return;
    }

    try {
      // Fetch profile
      const profRes = await fetchWithAuth(API_ENDPOINTS.PARTNER);
      const prof = await profRes.json();
      console.log("Profile data:", prof);
      setProfile(prof);

      // Fetch events
      const evRes = await fetchWithAuth(API_ENDPOINTS.EVENT_PERFORMER_BUSINESS);
      const evData = await evRes.json();
      console.log("Events data:", evData);
      setEvents(evData || []);
    } catch (err) {
      console.error("Error fetching data:", err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle Live Toggle
  const handleLiveToggle = (id) => {
    const event = events.find((v) => v.id === id);

    // Check if another event is live
    const activeEvent = events.find(
      (v) => v.status === "active" && v.id !== id,
    );
    if (event.status === "active") {
      setSelectedEventForLive(id);
      setShowEndLiveConfirm(true);
    } else if (event.status === "approved" || event.status === "paused") {
      if (activeEvent) {
        toast.error(
          `Cannot go live. Event "${activeEvent.name}" is currently active.`,
        );
        return;
      }
      setSelectedEventForLive(id);
      setShowLiveConfirm(true);
    } else {
      toast.error("Event must be approved before going live");
    }
  };

  // Image click handler
  // Image click handler
  const handleClick = (event) => {
    console.log("Event clicked:", event);

    // Only allow entry if event is live/active
    if (event.status === "active") {
      navigate(`/businessEventDetails/${event.venueId}`);
      localStorage.setItem("selectedEventId", event.id);
    } else if (event.status === "approved") {
      toast.info("Make Event live to enter");
    } else if (event.status === "paused") {
      toast.info("Make Event live to enter");
    } else if (event.status === "requested") {
      toast.error("Event is pending approval. Cannot enter yet");
    } else if (event.status === "rejected") {
      toast.error("This event has been rejected");
    } else if (event.status === "finished") {
      toast.error("This event has ended");
    } else {
      toast.error("Event must be live to enter");
    }
  };

  // Update event status
  const updateEventStatus = async (eventId, action) => {
    try {
      // Convert the action for API
      const apiAction = action === "live" ? "active" : action;

      console.log("Updating status:", eventId, apiAction);

      const response = await fetchWithAuth(
        API_ENDPOINTS.EVENT_STATUS(eventId),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ action: apiAction }), // Use apiAction here
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update status");
      }

      const updatedEvent = await response.json();
      console.log("Status update response:", updatedEvent);

      await fetchData(); // Refresh events list
      toast.success(`Event ${action} successfully`);
      return true;
    } catch (err) {
      console.error("Error updating event status:", err);
      toast.error("Another event is already live in this venue");
      return false;
    }
  };
  // Add extend session function
  const extendSession = async (eventId, timeValue, timeUnit) => {
    try {
      const token = localStorage.getItem("tringAuthToken");
      const event = events.find((v) => v.id === eventId);
      const currentEndTime = new Date(event.endAt);
      let newEndTime;

      // Calculate new end time
      if (timeUnit === "minutes") {
        newEndTime = new Date(currentEndTime.getTime() + timeValue * 60000);
      } else {
        newEndTime = new Date(
          currentEndTime.getTime() + timeValue * 60 * 60000,
        );
      }

      // Update the event
      const response = await fetchWithAuth(API_ENDPOINTS.EVENT_BY_ID(eventId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          endAt: newEndTime.toISOString(),
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to extend session: ${errorText}`);
      }

      const updatedEvent = await response.json();
      console.log("Session extended:", updatedEvent);

      // Refresh events list
      await fetchData();
      setShowExtendModal(false);

      const timeText =
        timeUnit === "minutes"
          ? `${timeValue} minute${timeValue > 1 ? "s" : ""}`
          : `${timeValue} hour${timeValue > 1 ? "s" : ""}`;
      toast.success(`Session extended for ${timeText} successfully!`);
    } catch (err) {
      console.error("Error extending session:", err);
      toast.error("Failed to extend session. Please try again.");
    }
  };
  // Sidebar navigation
  const handleSidebarNav = (page) => {
    setSidebarPage(page);
    switch (page) {
      case "home":
        navigate("/loggedinartistbussiness");
        break;
      case "calendar":
        navigate("/businessCalendar");
        break;
      case "qr":
        navigate("/BusinessQr");
        break;
      case "profile":
        navigate("/businessProfile");
        break;
      default:
        break;
    }
  };

  // Handle add event success
  const handleAddEventSuccess = async () => {
    const token = localStorage.getItem("tringAuthToken");
    if (!token) {
      navigate("/");
      return;
    }

    try {
      const evRes = await fetchWithAuth(API_ENDPOINTS.EVENT_PERFORMER_BUSINESS);
      const evData = await evRes.json();
      console.log("Refreshed events after add:", evData);
      setEvents(evData || []);
      window.location.reload();
    } catch (err) {
      console.error("Error refreshing events:", err);
      toast.error("Failed to refresh events list");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };
  // Add this function near the top of your component
  const isAnotherEventLive = (currentEventId) => {
    return events.some(
      (event) =>
        (event.status === "active" || event.status === "paused") &&
        event.id !== currentEventId,
    );
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-white via-[#F8F4FB] to-[#F0EBF5] w-screen relative md:mt-0 mt-20">
      <MobileNBottomNav />

      {/* Mobile Header */}
      <div className="fixed top-0 left-0 z-50 w-full px-2 py-3 flex items-center justify-between bg-logo backdrop-blur-sm lg:hidden">
        <div className="flex items-center gap-2">
          <img alt="" src="/images/tringbox-logo.png" className="h-6" />
        </div>

        <motion.button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="p-2 rounded-md"
          aria-label="Toggle sidebar"
        >
          {isSidebarOpen ? (
            <X size={24} className="text-white" />
          ) : (
            <Menu size={24} className="text-white" />
          )}
        </motion.button>
      </div>

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-0 w-80 h-full z-40 transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <Sidebar currentPage={sidebarPage} onPageChange={handleSidebarNav} />
      </div>

      {/* Toast Container */}
      <ToastContainer position="top-center" autoClose={3000} />

      {/* Main Content */}
      <main className="flex-1 min-h-screen lg:ml-80 w-full flex flex-col">
        {/* Header with Search */}
        <header className="bg-white/80 backdrop-blur-sm border-b border-purple-100 py-3 lg:py-4 sticky top-0 z-30 w-full px-3 sm:px-4 lg:px-6">
          {/* Profile Section */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-1 lg:mb-1">
            <div className="flex items-center space-x-3 md:mb-4 mb-2 lg:mb-0">
              {selectedVenueId && venues ? (
                <img
                  alt=""
                  className="h-12 w-12 p-2 mb-1 bg-black rounded-full"
                  src={
                    venues.find((v) => v.id === selectedVenueId)?.logoUrl ||
                    venues[0]?.logoUrl
                  }
                />
              ) : (
                <img
                  alt=""
                  className="h-12 w-12 mb-1 bg-black rounded-full"
                  src={
                    venues.find((v) => v.id === selectedVenueId)?.logoUrl ||
                    venues[0]?.logoUrl
                  }
                />
              )}

              <div className="relative flex-1 lg:flex-none border border-purple-600 bg-white/50 rounded-md">
                <select
                  value={selectedVenueId}
                  onChange={handleVenueChange}
                  className="pl-4 pr-10 py-2 w-full lg:w-80 rounded-md bg-transparent focus:outline-none focus:ring-2 focus:ring-logo focus:border-purple-600 border-0 text-sm"
                >
                  {venues.length > 1 && <option value="">All Venues</option>}
                  {venues.map((venue) => (
                    <option key={venue.id} value={venue.id}>
                      {venue.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Search Section */}
            <div className="flex items-center space-x-2 lg:space-x-4 lg:mt-0">
              <div className="relative flex-1 lg:flex-none">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#4A4A4A]" />
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-12 py-2 w-full lg:w-80 rounded-xl border border-purple-200 bg-white/50 focus:outline-none focus:ring-2 focus:ring-logo focus:border-transparent text-sm"
                />
              </div>
            </div>
          </div>

          {/* Event Type Tabs */}
          <div className="flex space-x-2 lg:space-x-5 lg:px-2 overflow-x-auto scrollbar-hide md:mt-3 mt-2">
            {[
              { id: "all", label: "All Events" },
              { id: "requested", label: "Requested" },
              { id: "approved", label: "Approved" },
              { id: "rejected", label: "Rejected" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveFilter(tab.id)}
                type="button"
                className={`
                  px-3 lg:px-4 py-2 rounded-lg text-xs lg:text-sm font-bold transition-all whitespace-nowrap
                  ${
                    activeFilter === tab.id
                      ? "bg-logo text-white"
                      : "text-[#4A4A4A] hover:bg-gray-100"
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
            <div className="flex-1" />
            <button
              type="button "
              className="md:flex hidden flex items-center space-x-2 bg-gradient-to-r from-logo to-[#6720A3] text-white px-4 py-2 md:px-6 md:py-3 rounded-xl font-medium shadow-lg w-full md:w-auto justify-center"
              onClick={() => setShowAddEventModal(true)}
            >
              <Plus className="h-4 w-4 md:h-5 md:w-5" />
              <span>Add Event</span>
            </button>
          </div>
        </header>

        {/* Events List */}
        <section className="flex-1 p-3 sm:p-4 lg:p-6 w-full">
          <AnimatePresence>
            {displayed.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <img
                  src="/images/dj.svg"
                  alt="No events found"
                  className="w-64 h-64 opacity-70 bg text-center"
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:gap-3 gap-2 lg:gap-6 w-full mb-20">
                {displayed.map((event, idx) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ delay: idx * 0.05 }}
                    className="w-full h-full lg:max-h-40 sm:max-h-40 border border-neutral-600 relative bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  >
                    {/* Event Card Content */}
                    <div className="flex flex-col sm:flex-row w-full h-full">
                      {/* Event Image */}
                      <div className="relative w-full sm:w-1/3 h-full overflow-hidden cursor-pointer">
                        <div className="absolute top-2 right-2 z-10 sm:hidden">
                          <div
                            className={`
                              px-3 py-1 rounded-full text-xs font-medium
                              ${
                                event.status === "active"
                                  ? "bg-green-500"
                                  : event.status === "finished"
                                    ? "bg-red-500"
                                    : event.status === "requested"
                                      ? "bg-orange-500"
                                      : event.status === "approved"
                                        ? "bg-green-500"
                                        : event.status === "rejected"
                                          ? "bg-red-500"
                                          : event.status === "paused"
                                            ? "bg-orange-500"
                                            : "bg-logo"
                              }
                              text-white shadow-lg backdrop-blur-sm bg-opacity-90
                            `}
                          >
                            {event.status === "active"
                              ? "Live"
                              : event.status.charAt(0).toUpperCase() +
                                event.status.slice(1)}
                          </div>
                        </div>
                        <div onClick={() => handleClick(event)}>
                          <img
                            src={
                              event.photoUrl ||
                              event.venuePhotoUrl ||
                              "https://via.placeholder.com/400x200"
                            }
                            alt={event.name}
                            className="w-full lg:h-40 sm:h-40 h-40 object-cover"
                          />
                        </div>
                        <div className="absolute top-2 left-2 z-10">
                          <div className="px-3 py-1 rounded-full text-xs font-medium bg-black/60 text-white shadow-lg backdrop-blur-sm bg-opacity-90">
                            {formatDate(event.startAt)}{" "}
                            {formatTime(event.startAt)}
                          </div>
                        </div>
                      </div>

                      {/* Event Details */}
                      <div className="sm:w-2/3 p-3 lg:p-4 flex flex-col justify-between relative">
                        <div>
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                            <h3
                              className="text-lg lg:text-xl font-bold text-gray-900 cursor-pointer"
                              onClick={() => handleClick(event)}
                            >
                              {event.name}
                            </h3>
                            <div className="hidden sm:block">
                              <div
                                className={`
                                  px-3 py-1 rounded-full text-xs font-medium self-start
                                  ${
                                    event.status === "active"
                                      ? "bg-green-500"
                                      : event.status === "finished"
                                        ? "bg-red-500"
                                        : event.status === "requested"
                                          ? "bg-orange-500"
                                          : event.status === "approved"
                                            ? "bg-green-500"
                                            : event.status === "rejected"
                                              ? "bg-red-500"
                                              : event.status === "paused"
                                                ? "bg-orange-500"
                                                : "bg-logo"
                                  }
                                  text-white
                                `}
                              >
                                {event.status === "active"
                                  ? "Live"
                                  : event.status.charAt(0).toUpperCase() +
                                    event.status.slice(1)}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center text-xs lg:text-sm text-gray-500">
                            <MapPin className="h-3 w-3 lg:h-4 lg:w-4 mr-1 flex-shrink-0" />

                            <span className="truncate">
                              {event.venueAddress?.line1
                                ? `${event.venueAddress.line1}${
                                    event.venueAddress.city
                                      ? ", " + event.venueAddress.city
                                      : ""
                                  }${
                                    event.venueAddress.state
                                      ? ", " + event.venueAddress.state
                                      : ""
                                  }`
                                : "No address"}
                            </span>
                          </div>
                        </div>

                        {/* Controls */}
                        <div className="flex flex-wrap w-full items-center gap-2 lg:gap-3 md:mt-4 mt-3 lg:mt-0 lg:right-4">
                          {/* Live Toggle Switch with label */}
                          {(event.status === "active" ||
                            event.status === "approved" ||
                            event.status === "requested" ||
                            event.status === "paused") && (
                            <label className="inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={event.status === "active"}
                                onChange={() => handleLiveToggle(event.id)}
                                disabled={isAnotherEventLive(event.id)}
                                className="sr-only peer"
                              />
                              <div
                                className={`
          w-10 h-5 lg:w-12 lg:h-6 rounded-full relative
          after:content-[''] after:absolute after:top-0.5 after:left-[2px]
          after:bg-white after:border after:border-gray-300 after:rounded-full after:h-4 after:w-4 lg:after:h-5 lg:after:w-5 after:transition-all
          peer-checked:after:translate-x-5 lg:peer-checked:after:translate-x-6
          ${isAnotherEventLive(event.id) ? "opacity-50 cursor-not-allowed" : ""}
          peer-checked:bg-green-600 bg-gray-300
        `}
                              />
                              <span
                                className={`ml-2 text-xs lg:text-sm font-semibold ${
                                  event.status === "active"
                                    ? "text-green-600"
                                    : "text-gray-500"
                                }`}
                              >
                                {event.status === "active" ? "Live" : "Live"}
                              </span>
                            </label>
                          )}
                          <div className="flex-1" />

                          {/* Action Buttons */}
                          {(event.status === "active" ||
                            event.status === "approved" ||
                            event.status === "requested" ||
                            event.status === "paused") && (
                            <>
                              {/* Pause button for both active and approved states */}
                              {(event.status === "active" ||
                                event.status === "approved" ||
                                event.status === "requested") && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedEventId(event.id);
                                    setShowPauseConfirm(true);
                                  }}
                                  disabled={event.status !== "active"}
                                  className={`px-3 lg:px-4 py-2 border-2 border-logo text-logo rounded-lg font-medium flex items-center space-x-1 lg:space-x-2 hover:bg-logo hover:text-white transition-colors text-xs lg:text-sm ${
                                    event.status !== "active"
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }`}
                                >
                                  <Pause className="h-3 w-3 lg:h-4 lg:w-4" />
                                  <span>Pause</span>
                                </button>
                              )}

                              {/* Resume button for paused state */}
                              {event.status === "paused" && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedEventId(event.id);
                                    setShowResumeConfirm(true);
                                  }}
                                  disabled={isAnotherEventLive(event.id)}
                                  className={`px-3 lg:px-4 py-2 bg-logo text-white rounded-lg font-medium flex items-center space-x-1 lg:space-x-2 hover:bg-[#62187D] transition-colors text-xs lg:text-sm ${
                                    isAnotherEventLive(event.id)
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }`}
                                >
                                  <Play className="h-3 w-3 lg:h-4 lg:w-4" />
                                  <span>Resume</span>
                                </button>
                              )}

                              {/* End button - Always show for active, paused, and approved states */}
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedEventId(event.id);
                                  setShowEndConfirm(true);
                                }}
                                disabled={event.status !== "active"}
                                className={`px-3 lg:px-4 py-2 bg-red-500 text-white rounded-lg font-medium flex items-center space-x-1 lg:space-x-2 hover:bg-red-600 transition-colors text-xs lg:text-sm ${
                                  event.status !== "active"
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                                }`}
                              >
                                <LogOut className="h-3 w-3 lg:h-4 lg:w-4" />
                                <span>End</span>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
                {isLoadingMore && (
                  <div className="flex items-center justify-center w-full py-4">
                    <div className="animate-pulse text-logo text-sm font-bold">
                      Loading more events...
                    </div>
                  </div>
                )}
                {hasMore && !selectedVenueId && (
                  <div className="flex justify-center mt-6 col-span-full">
                    <button
                      onClick={fetchMoreEvents}
                      disabled={isLoadingMore}
                      className="bg-logo text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-800 transition-colors disabled:opacity-50"
                    >
                      {isLoadingMore ? "Loading..." : "Show More"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </AnimatePresence>
        </section>

        {/* Floating Add Button - Mobile */}
        <motion.button
          type="button"
          className="fixed bottom-20 right-4 md:hidden block lg:bottom-6 lg:right-6 bg-gradient-to-r from-logo to-[#6720A3] text-white p-3 lg:p-4 rounded-full shadow-lg z-50"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setShowAddEventModal(true)}
        >
          <Plus className="h-5 w-5 lg:h-6 lg:w-6" />
        </motion.button>

        {/* Modals */}
        <Modal
          isOpen={showAddEventModal}
          onClose={() => setShowAddEventModal(false)}
        >
          <AddEventFormFields
            onSuccess={handleAddEventSuccess}
            setAddEvent={setShowAddEventModal}
          />
        </Modal>

        {/* Pause Confirmation Modal */}
        <Modal
          isOpen={showPauseConfirm}
          onClose={() => setShowPauseConfirm(false)}
        >
          <div className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Pause Session
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to pause this session?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowPauseConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  updateEventStatus(selectedEventId, "paused");
                  setShowPauseConfirm(false);
                }}
                className="px-4 py-2 bg-logo text-white rounded-lg font-medium hover:bg-[#62187D]"
              >
                Confirm Pause
              </button>
            </div>
          </div>
        </Modal>

        {/* Resume Confirmation Modal */}
        <Modal
          isOpen={showResumeConfirm}
          onClose={() => setShowResumeConfirm(false)}
        >
          <div className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Resume Session
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to resume this session?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowResumeConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await updateEventStatus(selectedEventId, "active");
                  setShowResumeConfirm(false);
                  // Navigate to event details after resuming
                  const event = events.find((e) => e.id === selectedEventId);
                  if (event) {
                    navigate(`/businessEventDetails/${event.venueId}`);
                    localStorage.setItem("selectedEventId", event.id);
                  }
                }}
                className="px-4 py-2 bg-logo text-white rounded-lg font-medium hover:bg-[#62187D]"
              >
                Confirm Resume
              </button>
            </div>
          </div>
        </Modal>

        {/* End Confirmation Modal */}
        <Modal isOpen={showEndConfirm} onClose={() => setShowEndConfirm(false)}>
          <div className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              End Session
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to end this session? This action cannot be
              undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowEndConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  updateEventStatus(selectedEventId, "finished");
                  setShowEndConfirm(false);
                }}
                className="px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600"
              >
                End Session
              </button>
            </div>
          </div>
        </Modal>

        {/* Go Live Confirmation Modal */}

        {showLiveConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", duration: 0.5 }}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Play className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Start Live Session
                </h3>
                <p className="text-gray-600 mb-2">
                  Are you sure you want to start this live session?
                </p>
                <div className="bg-blue-50 rounded-lg md:p-4 p-2 text-sm text-blue-800">
                  <ul className="list-disc md:list-inside md:space-y-1 space-y-2 md:text-center text-start">
                    <li>Users will be able to make song requests</li>
                    <li>You can manage requests and playlists</li>
                    <li>Session can be paused or ended if needed</li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
                  onClick={() => setShowLiveConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 py-3 px-4 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                  onClick={async () => {
                    const ok = await updateEventStatus(
                      selectedEventForLive,
                      "active",
                    );
                    setShowLiveConfirm(false);
                    // Navigate to event details after going live
                    const event = events.find(
                      (e) => e.id === selectedEventForLive,
                    );

                    if (event && ok) {
                      navigate(`/businessEventDetails/${event.venueId}`);
                      localStorage.setItem("selectedEventId", event.id);
                    }
                  }}
                >
                  <Play className="w-4 h-4" />
                  Start Live
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* End Live Confirmation Modal */}
        {showEndLiveConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", duration: 0.5 }}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <LogOut className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  End Live Session
                </h3>
                <p className="text-gray-600 mb-2">
                  Are you sure you want to end this live session?
                </p>
                <div className="bg-red-50 rounded-lg md:p-4 p-2 text-sm text-red-800 text-left">
                  <ul className="list-disc list-inside space-y-1">
                    <li>All current requests will be lost</li>
                    <li>Users won't be able to make new requests</li>
                    <li>Session cannot be resumed once ended</li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
                  onClick={() => setShowEndLiveConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 py-3 px-4 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                  onClick={() => {
                    updateEventStatus(selectedEventForLive, "finished");
                    setShowEndLiveConfirm(false);
                  }}
                >
                  <LogOut className="w-4 h-4" />
                  End Session
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Extend Session Modal */}
        {showExtendModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", duration: 0.5 }}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Plus className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Extend Session
                </h3>
                <p className="text-gray-600 mb-6">
                  Choose how long you want to extend the session:
                </p>

                <div className="grid grid-cols-2 gap-3 mb-6">
                  {timeOptions.map((option) => (
                    <button
                      key={`${option.value}${option.unit}`}
                      onClick={() => {
                        setExtensionTime(option.value);
                        setExtensionUnit(option.unit);
                      }}
                      className={`
                      py-4 px-6 rounded-xl font-medium transition-all
                      ${
                        extensionTime === option.value &&
                        extensionUnit === option.unit
                          ? "bg-blue-500 text-white ring-2 ring-blue-500 ring-offset-2"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }
                    `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-800">
                  <p className="font-medium mb-1">Selected extension:</p>
                  <p className="text-lg font-bold">
                    {extensionTime}{" "}
                    {extensionUnit === "minutes"
                      ? "minutes"
                      : extensionTime === 1
                        ? "hour"
                        : "hours"}
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
                  onClick={() => setShowExtendModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 py-3 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                  onClick={() => {
                    extendSession(
                      selectedEventForLive,
                      extensionTime,
                      extensionUnit,
                    );
                  }}
                >
                  <Plus className="w-4 h-4" />
                  Extend Session
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </main>
    </div>
  );
}
